import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const AuthContext = createContext()

// Get the backend API URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3667/api'

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    // Check if user is logged in on mount
    useEffect(() => {
        const token = localStorage.getItem('token')
        if (token) {
            console.log('Token found in localStorage, verifying with backend...')
            // Verify token with backend
            verifyToken()
            
            // Add a timeout to prevent infinite loading
            const timeout = setTimeout(() => {
                console.warn('Token verification timeout, setting loading to false')
                setLoading(false)
            }, 10000) // 10 second timeout
            
            return () => clearTimeout(timeout)
        } else {
            console.log('No token found in localStorage')
            setLoading(false)
        }
    }, [])

    const verifyToken = async (retryCount = 0) => {
        try {
            const token = localStorage.getItem('token')
            if (!token) {
                setLoading(false)
                return
            }

            console.log('Verifying token with backend... (attempt:', retryCount + 1, ')')
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })

            if (response.ok) {
                const data = await response.json()
                console.log('Token verification successful, user:', data.data.user)
                setUser(data.data.user)
                setLoading(false) // Set loading to false on success
            } else if (response.status === 401) {
                // Token is invalid (401 Unauthorized)
                console.log('Token is invalid (401), removing token')
                localStorage.removeItem('token')
                setUser(null)
                setLoading(false) // Set loading to false on invalid token
            } else {
                // Other errors (network, server down, etc.) - keep the token for now
                console.warn('Token verification failed but keeping token:', response.status, response.statusText)
                
                // Retry up to 2 times for network/server errors
                if (retryCount < 2) {
                    console.log('Retrying token verification in 2 seconds...')
                    setTimeout(() => {
                        verifyToken(retryCount + 1)
                    }, 2000)
                    return // Don't set loading to false yet
                } else {
                    console.warn('Max retries reached, keeping token for offline access')
                    setLoading(false) // Set loading to false after max retries
                }
            }
        } catch (error) {
            console.error('Token verification error:', error)
            
            // Retry up to 2 times for network errors
            if (retryCount < 2) {
                console.log('Retrying token verification in 2 seconds due to network error...')
                setTimeout(() => {
                    verifyToken(retryCount + 1)
                }, 2000)
                return // Don't set loading to false yet
            } else {
                console.warn('Max retries reached, keeping token for offline access')
            }
        } finally {
            if (retryCount >= 2) {
                setLoading(false)
            }
        }
    }

    const login = async (email, password) => {
        try {
            console.log('Making login request to:', `${API_BASE_URL}/auth/login`)
            console.log('Request payload:', { email, password: '***' })

            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            })

            console.log('Response status:', response.status)
            console.log('Response headers:', response.headers)

            const responseText = await response.text()
            console.log('Response text:', responseText)

            let data
            try {
                data = JSON.parse(responseText)
            } catch (parseError) {
                console.error('Failed to parse JSON response:', parseError)
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 100)}...`)
            }

            if (!response.ok) {
                throw new Error(data.message || 'Login failed')
            }

            // Store token and user data
            localStorage.setItem('token', data.data.token)
            setUser(data.data.user)

            // Redirect to camera monitoring
            navigate('/camera-monitoring')
        } catch (error) {
            console.error('Login error:', error)
            throw error
        }
    }

    const signup = async (username, email, password) => {
        try {
            console.log('Making signup request to:', `${API_BASE_URL}/auth/signup`)
            console.log('Request payload:', { username, email, password: '***' })

            const response = await fetch(`${API_BASE_URL}/auth/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, email, password })
            })

            console.log('Response status:', response.status)
            console.log('Response headers:', response.headers)

            const responseText = await response.text()
            console.log('Response text:', responseText)

            let data
            try {
                data = JSON.parse(responseText)
            } catch (parseError) {
                console.error('Failed to parse JSON response:', parseError)
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 100)}...`)
            }

            if (!response.ok) {
                throw new Error(data.message || 'Signup failed')
            }

            // Store token and user data
            localStorage.setItem('token', data.data.token)
            setUser(data.data.user)

            // Redirect to camera monitoring
            navigate('/camera-monitoring')
        } catch (error) {
            console.error('Signup error:', error)
            throw error
        }
    }

    const logout = () => {
        localStorage.removeItem('token')
        localStorage.removeItem('cameraPreferences') // Clear preferences on logout
        setUser(null)
        navigate('/login')
    }

    const hasPermission = (resource, action) => {
        if (!user || !user.role) return false

        // Super admin has all permissions
        if (user.role.name?.toLowerCase() === 'super admin') return true

        // Check specific permission
        return user.role.permissions?.[resource]?.[action] || false
    }

    const isSuperAdmin = () => {
        const isAdmin = user?.role?.name?.toLowerCase() === 'super admin'
        console.log('isSuperAdmin check:', {
            userRole: user?.role?.name,
            userRoleLower: user?.role?.name?.toLowerCase(),
            isAdmin: isAdmin,
            user: user?.username
        })
        return isAdmin
    }

    const updateProfile = async (profileData) => {
        try {
            const token = localStorage.getItem('token')
            if (!token) {
                throw new Error('No authentication token found')
            }

            console.log('Updating profile with data:', profileData)
            console.log('API URL:', `${API_BASE_URL}/auth/me`)
            console.log('Token:', token ? 'Present' : 'Missing')

            const response = await fetch(`${API_BASE_URL}/auth/me`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profileData)
            })

            console.log('Response status:', response.status)
            console.log('Response headers:', Object.fromEntries(response.headers.entries()))

            const responseText = await response.text()
            console.log('Update profile response:', responseText)

            let data
            try {
                data = JSON.parse(responseText)
            } catch (parseError) {
                console.error('Failed to parse JSON response:', parseError)
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 100)}...`)
            }

            if (!response.ok) {
                console.error('Profile update failed:', data)
                throw new Error(data.message || 'Profile update failed')
            }

            // Update user data in context
            setUser(data.data.user)
            console.log('Profile updated successfully:', data.data.user)
            
            return data
        } catch (error) {
            console.error('Update profile error:', error)
            throw error
        }
    }

    const changePassword = async (currentPassword, newPassword) => {
        try {
            const token = localStorage.getItem('token')
            if (!token) {
                throw new Error('No authentication token found')
            }

            console.log('Changing password...')

            const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ currentPassword, newPassword })
            })

            const responseText = await response.text()
            console.log('Change password response:', responseText)

            let data
            try {
                data = JSON.parse(responseText)
            } catch (parseError) {
                console.error('Failed to parse JSON response:', parseError)
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 100)}...`)
            }

            if (!response.ok) {
                throw new Error(data.message || 'Password change failed')
            }

            console.log('Password changed successfully')
            return data
        } catch (error) {
            console.error('Change password error:', error)
            throw error
        }
    }

    const value = {
        user,
        setUser,
        loading,
        login,
        signup,
        logout,
        updateProfile,
        changePassword,
        hasPermission,
        isSuperAdmin,
        verifyToken, // Export for manual verification if needed
        // Debug function to force loading to false
        forceStopLoading: () => {
            console.log('Force stopping loading state')
            setLoading(false)
        }
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
} 